import * as React from 'react';

import { cn } from '../utils/cn';

export type CountPillProps = {
  label: React.ReactNode;
  count?: number | string | null;
  active?: boolean;
  /** Soften when inactive and empty (list filter rules). */
  dimmed?: boolean;
  /** Optional status/dot color (CSS color value). */
  dotColor?: string;
  onClick?: () => void;
  className?: string;
  type?: 'button' | 'submit';
  disabled?: boolean;
};

/**
 * Rounded count chip for list toolbars (status / facet filters).
 * Active = inverted foreground fill; inactive = card + surface border.
 * Forwards refs so it works as `PopoverTrigger asChild`.
 */
export const CountPill = React.forwardRef<HTMLButtonElement, CountPillProps>(
  function CountPill(
    {
      label,
      count,
      active = false,
      dimmed = false,
      dotColor,
      onClick,
      className,
      type = 'button',
      disabled = false,
    },
    ref,
  ) {
    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        onClick={onClick}
        className={cn(
          'inline-flex h-[30px] shrink-0 items-center gap-1.5 rounded-full border px-[11px] text-[12.5px] whitespace-nowrap transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50',
          active
            ? 'border-foreground bg-foreground font-semibold text-background'
            : 'border-[color:var(--surface-border)] bg-card text-foreground hover:bg-muted/40',
          dimmed && 'opacity-45',
          className,
        )}
      >
        {dotColor && !active ? (
          <span
            className="size-1.5 shrink-0 rounded-full"
            style={{ backgroundColor: dotColor }}
            aria-hidden
          />
        ) : null}
        <span>{label}</span>
        {count != null && count !== '' ? (
          <span
            className={cn(
              'font-mono text-[11px]',
              active ? 'opacity-65' : 'text-muted-foreground',
            )}
          >
            {count}
          </span>
        ) : null}
      </button>
    );
  },
);
